import React from "react";
import { Button, FormSize } from "ui/ecosystems/forms";
import styled from "styled-components";

import { ExternalLink } from "ui/Link";

export function InstagramConnectInstructions({
  className,
  ButtonProps,
}: {
  className?: string;
  ButtonProps: { disabled: boolean; onClick(): void };
}) {
  return (
    <Root className={className}>
      <Grid>
        <TopPanel>
          <p style={{ lineHeight: 1.5 }}>
            For Instagram scheduling, Sked can only connect Instagram Business
            accounts. As every Instagram Business account is associated with a
            Facebook Page,{" "}
            <b>you will need to give us permission to access both.</b>
          </p>
        </TopPanel>
        <LeftPanel>
          <StepList>
            <StepListRow label="Step 1:">
              <p>
                In the Facebook modal, give access to Sked by selecting the
                Instagram Business accounts you want to connect.
              </p>
            </StepListRow>
            <StepListRow label="Step 2:">
              <p>
                Select the associated Facebook Pages with the Instagram Business
                accounts to authorise Sked’s connection.
              </p>
            </StepListRow>
            <StepListRow label="Step 3:">
              <p>
                Select all the Instagram Business accounts to connect with Sked.
              </p>
            </StepListRow>
          </StepList>
          <ButtonContainer>
            <Button primary size={FormSize.large} {...ButtonProps}>
              Connect Instagram Business account
            </Button>
          </ButtonContainer>
          <Disclaimer>
            <p>
              Need to convert to an Instagram Business account?{" "}
              <ExternalLink href="https://help.skedsocial.com/en/articles/3811709-setting-up-your-instagram-business-account-and-facebook-page-for-sked">
                Here are the instructions
              </ExternalLink>
              .
            </p>
            <p>
              <ExternalLink href="https://www.facebook.com/settings?tab=business_tools">
                Edit existing permissions
              </ExternalLink>{" "}
              for Facebook Pages or Instagram Business accounts if you have
              previously connected Sked.
            </p>
            <p>
              We only access information about your Facebook Pages and
              associated Instagram Business accounts. You can remove access at
              any time{" "}
              <ExternalLink href="https://www.facebook.com/settings?tab=business_tools">
                through Facebook
              </ExternalLink>
              .
            </p>
          </Disclaimer>
        </LeftPanel>
        <RightPanel>
          <img
            src="/assets/img/connect-ig-biz-acc.gif"
            alt="Log in with Facebook instruction"
            style={{
              maxWidth: 270,
              filter: "drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.1))",
              borderRadius: 3,
            }}
          />
        </RightPanel>
      </Grid>
    </Root>
  );
}

const Root = styled.div`
  font-size: 14px;
  line-height: 1.2;

  p {
    margin: 0;
    font-size: inherit;
    line-height: inherit;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 24px;
`;

const TopPanel = styled.div`
  grid-column: 1 / span 2;
`;

const LeftPanel = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-gap: 12px;
`;

const RightPanel = styled.div``;

const StepList = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 8px;
  grid-column-gap: 16px;
`;

function StepListRow({
  label,
  children,
}: {
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <p>{label}</p>
      <div>{children}</div>
    </>
  );
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 16px;
`;

const Disclaimer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 12px;
  border-top: 1px solid #eee;
  font-size: 12px;

  & > *:not(:first-child) {
    margin-top: 8px;
  }
`;
